import type { ProblemTemplate } from '../../types.ts';
import {
  columnSlip,
  countingRun,
  lerp,
  scaled,
  sequencePrompt,
  smallerFromLarger,
  unknownLetter,
  wholeProblem,
} from './helpers-g01-04.ts';

/**
 * Grade 4 templates — ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE.
 *
 * CAPS Grade 4: letters start to stand for unknown numbers (number sentences),
 * multiplication/division facts to 12 × 12, patterns with a constant difference
 * or doubling, and number sentences with brackets. Still no negatives.
 */

const varAdd: ProblemTemplate = {
  id: 'g4.var-add',
  grade: 4,
  title: 'Solve n + b = c',
  parTimeMs: 9000,
  generate(rng, difficulty) {
    const v = unknownLetter(rng, difficulty);
    const shown = rng.int(scaled(6, 15, difficulty), scaled(25, 90, difficulty));
    const answer = rng.intExcept(scaled(4, 12, difficulty), scaled(25, 110, difficulty), [shown]);
    const total = answer + shown; // ≤ 50 at difficulty 0, ≤ 200 at difficulty 1
    const prompt = rng.chance(0.35) ? `${shown} + ${v} = ${total}` : `${v} + ${shown} = ${total}`;
    return wholeProblem(
      prompt,
      answer,
      [
        total + shown, // added instead of subtracting (inverse-operation error)
        smallerFromLarger(total, shown), // didn't borrow: took smaller digit from larger in each column
        answer + 10, // borrowed a ten but forgot to reduce the tens
        answer - 10, // miscounted the tens
      ],
      { key: 'solve', params: { v } },
    );
  },
};

const varMult: ProblemTemplate = {
  id: 'g4.var-mult',
  grade: 4,
  title: 'Solve a × n = c (tables to 12)',
  parTimeMs: 9000,
  generate(rng, difficulty) {
    const v = unknownLetter(rng, difficulty);
    const maxFactor = scaled(6, 12, difficulty);
    const shown = rng.int(2, maxFactor);
    const answer = rng.intExcept(2, maxFactor, [shown]); // 7 × n = 49 would show the answer
    const product = shown * answer;
    const prompt = rng.chance(0.35) ? `${v} \\times ${shown} = ${product}` : `${shown} \\times ${v} = ${product}`;
    return wholeProblem(
      prompt,
      answer,
      [
        product - shown, // treated × like + and subtracted
        answer + 1, // slipped to the neighbouring fact in the table
        answer - 1, // slipped to the neighbouring fact in the table
      ],
      { key: 'solve', params: { v } },
    );
  },
};

const varDiv: ProblemTemplate = {
  id: 'g4.var-div',
  grade: 4,
  title: 'Solve n ÷ a = b',
  parTimeMs: 10000,
  generate(rng, difficulty) {
    const v = unknownLetter(rng, difficulty);
    const divisor = rng.int(2, scaled(5, 10, difficulty));
    const quotient = rng.int(2, scaled(6, 12, difficulty));
    const answer = divisor * quotient;
    return wholeProblem(
      `${v} \\div ${divisor} = ${quotient}`,
      answer,
      [
        quotient / divisor, // divided again instead of multiplying (dropped when not whole)
        divisor + quotient, // added instead of multiplying
        divisor * (quotient + 1), // slipped to the neighbouring multiple
        divisor * (quotient - 1), // slipped to the neighbouring multiple
      ],
      { key: 'solve', params: { v } },
    );
  },
};

const patternRule: ProblemTemplate = {
  id: 'g4.pattern-rule',
  grade: 4,
  title: 'Patterns: add a constant or double',
  parTimeMs: 9000,
  generate(rng, difficulty) {
    if (rng.chance(lerp(0.3, 0.5, difficulty))) {
      // Doubling: 3, 6, 12, 24, □
      const start = rng.int(1, scaled(3, 12, difficulty));
      const terms = [start, 2 * start, 4 * start, 8 * start];
      const last = 8 * start;
      return wholeProblem(
        sequencePrompt(terms),
        16 * start,
        [
          last + (last - 4 * start), // repeated the last difference (treated doubling as adding)
          last + 2, // added 2 instead of multiplying by 2
          32 * start, // skipped a term
        ],
        { key: 'next' },
      );
    }
    // Constant difference, from any starting number; sometimes decreasing.
    const step = rng.int(scaled(3, 6, difficulty), scaled(9, 25, difficulty));
    const limit = Math.max(scaled(60, 200, difficulty), 6 * step);
    const run = countingRun(rng, step, 5, limit, rng.int(0, step - 1));
    const backward = rng.chance(lerp(0, 0.35, difficulty));
    const ordered = backward ? run.slice().reverse() : run;
    const dir = backward ? -1 : 1;
    const last = ordered[3]!;
    const answer = ordered[4]!;
    return wholeProblem(
      sequencePrompt(ordered.slice(0, 4)),
      answer,
      [
        last + dir * (step + 1), // worked out the difference one too big
        last + dir * (step - 1), // worked out the difference one too small
        answer + dir * step, // skipped a term
        columnSlip(last, step, backward), // forgot to carry / didn't borrow in the column
        ...(backward ? [last + step] : [2 * last]), // assumed the pattern goes up / doubles
      ],
      { key: 'next' },
    );
  },
};

const bracketMissing: ProblemTemplate = {
  id: 'g4.bracket-missing',
  grade: 4,
  title: 'Missing number in brackets: (a − □) × b = c',
  parTimeMs: 14000,
  generate(rng, difficulty) {
    const factor = rng.int(2, scaled(5, 10, difficulty));
    const inside = rng.int(2, scaled(5, 10, difficulty)); // value of the bracket
    const product = inside * factor;
    // ≠ inside so "stopped after dividing" stays wrong; ≠ factor and product so the answer
    // can't be copied from the prompt.
    const answer = rng.intExcept(2, scaled(6, 15, difficulty), [inside, factor, product]);
    const start = inside + answer;
    const bracket = `(${start} - \\box)`;
    const prompt = rng.chance(lerp(0, 0.4, difficulty))
      ? `${factor} \\times ${bracket} = ${product}`
      : `${bracket} \\times ${factor} = ${product}`;
    return wholeProblem(prompt, answer, [
      inside, // divided to find the bracket's value, then stopped
      start + inside, // added instead of subtracting in the last step
      start - product, // ignored the × and "undid" only the subtraction (dropped when negative)
      answer + 1, // off by one
    ]);
  },
};

export const GRADE_04: ProblemTemplate[] = [varAdd, varMult, varDiv, patternRule, bracketMissing];
